import {
  booleanAttribute,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  OnInit,
  Signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { distinctUntilChanged, filter, merge, tap } from 'rxjs';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { DialogService } from 'src/app/shared/dialog/services/dialog/dialog.service';
import { FormFieldComponent } from 'src/app/shared/form-field/components/form-field/form-field.component';
import { FormFieldsDirective } from 'src/app/shared/form-field/directives/form-fields/form-fields.directive';
import { FormFieldOptions } from 'src/app/shared/form-field/form-field.types';
import { FormFieldService } from 'src/app/shared/form-field/services/form-field/form-field.service';
import { CaRequestDto } from 'src/app/shared/services/api/dto/ca-request-dto';
import { LangService } from 'src/app/shared/services/lang/lang.service';
import {
  CaFormField,
  caFormValueToDto,
  caToFormValue,
  formValueToUpdateDto,
  SIGNED_BY_EXTERNAL,
  ValidityUnit,
} from './create-ca.types';
import { CA } from 'src/app/shared/entities/ca';
import { UpdateCaRequestDto } from 'src/app/shared/services/api/dto/update-ca-request-dto';
import { syncExpirationDateAndDuration } from 'src/app/shared/util/sync-expiration-date-and-duration';
import { ValidationConstants } from 'src/app/shared/validators/validation-constants';
import { ErrorStateMatcher } from '@angular/material/core';
import { Profile } from 'src/app/shared/entities/profile';
import { ProfileType } from 'src/app/shared/entities/profile-type';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MonospacedBlockComponent } from 'src/app/shared/components/monospaced-block/monospaced-block.component';
import { signingAlgos } from 'src/app/shared/entities/signing-algos';

@Component({
  selector: 'app-create-ca',
  imports: [
    MatButton,
    ReactiveFormsModule,
    SpinnerComponent,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    FormFieldComponent,
    FormFieldsDirective,
    MonospacedBlockComponent,
  ],
  providers: [FormFieldService],
  templateUrl: './create-ca.component.html',
  styleUrl: './create-ca.component.scss',
})
export class CreateCaComponent implements OnInit {
  formFieldService = inject(FormFieldService);
  private dialogService = inject(DialogService);
  private lang = inject(LangService).lang;
  labels = this.lang.features.calist.create;
  signingAlgos = signingAlgos;

  creating = input<boolean | undefined>(false);
  edit = input(false, { transform: booleanAttribute });
  editValue = input<CA>();
  cryptotokens = input<string[]>([]);
  profiles = input<Profile[]>([]);
  caNames = input<string[]>([]);
  cryptotokenKeys = input<{ token: string; keys: string[] }>();
  loadingCryptotokenKeys = input(false);
  csr = input<string>();
  loadingCsr = input(false);
  importedCertChain = input(false);
  importingCertChain = input(false);
  needCryptotokenKeys = output<{ tokenName: string }>();
  needCsr = output<{ caName: string }>();
  create = output<CaRequestDto>();
  update = output<{ ca: CA; dto: UpdateCaRequestDto }>();
  cancelCreate = output();
  importCertChain = output<{ caName: string; certChain: string }>();

  title = computed(() => {
    const edit = this.edit();
    const editValue = this.editValue();
    if (edit) {
      return this.labels.editTitle(editValue?.caName ?? '');
    }
    return this.labels.title;
  });

  canUseCsr: Signal<boolean> = computed(() => {
    // Sync-up 2025-09-20: CSR can be generated for any CA in the Edit mode
    const edit = this.edit();
    return edit;
  });

  canImportCertChain: Signal<boolean> = computed(() => {
    // Sync-up 2025-09-20: Cert chain can be imported in the Edit mode only if
    // certificateChain is null.  It can be imported only once.
    //
    // We show the input field if the certificateChain is null and disable the
    // button after a successful import.
    const edit = this.edit();
    const editValue = this.editValue();
    const certChain = editValue?.certificateChain;
    return edit && !certChain;
  });

  private selfSignedOption = this.labels.selfSignedOption;
  private defaultSigningOptions = [this.selfSignedOption, SIGNED_BY_EXTERNAL];

  caNamesWithExternal = computed(() => {
    const caNames = this.caNames();
    return caNames
      ? [...this.defaultSigningOptions, ...caNames]
      : this.defaultSigningOptions;
  });

  CaFormField = CaFormField;
  ValidityUnit = ValidityUnit;

  private fb = inject(FormBuilder);

  private signedByInitialValue = this.selfSignedOption;

  form = this.fb.group({
    [CaFormField.subjectDn]: ['', [Validators.required]],
    [CaFormField.profileName]: ['', [Validators.required]],
    [CaFormField.tokenName]: ['', [Validators.required]],
    [CaFormField.signKey]: ['', [Validators.required]],
    [CaFormField.signingAlgo]: ['', [Validators.required]],
    [CaFormField.signedBy]: [this.signedByInitialValue],
    [CaFormField.validityUnits]: new FormControl<string>(ValidityUnit.year, [
      Validators.required,
    ]),
    [CaFormField.validityDays]: [''],
    [CaFormField.validityYears]: [''],
    [CaFormField.expirationDate]: this.fb.control<Date | null>(null, [
      Validators.required,
      // min and max validators are added automatically by the date picker library
    ]),
    [CaFormField.defaultCRLDistPoint]: [
      '',
      [Validators.pattern(ValidationConstants.urlRegExp)],
    ],
    [CaFormField.defaultOCSPServiceLocator]: [''],
    [CaFormField.useLdapDnOrder]: [true], // requested in https://bahar-limaye.atlassian.net/browse/SXTE-1021
    [CaFormField.certChain]: [''],
  });

  // The value of the control may be set programmatically.  Thus, if the
  // owlDateTime{Min,Max} errors are present, show the error message, even
  // though the control is untouched.
  expirationErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: control =>
      (control?.touched && !!control?.errors) ||
      !!control?.hasError('owlDateTimeMax') ||
      !!control?.hasError('owlDateTimeMin'),
  };

  private editableFields = [
    CaFormField.defaultCRLDistPoint,
    CaFormField.defaultOCSPServiceLocator,
    CaFormField.useLdapDnOrder,
    CaFormField.certChain,
  ];

  private tokenName = toSignal(
    this.form.controls[CaFormField.tokenName].valueChanges.pipe(
      distinctUntilChanged(),
    ),
    {
      initialValue: null,
    },
  );

  private rootProfileNames = computed(() => {
    const profiles = this.profiles();
    return profiles
      .filter(
        profile =>
          profile.profileName && profile.profileType === ProfileType.ROOT_CA,
      )
      .map(profile => profile.profileName ?? '');
  });

  private subProfileNames = computed(() => {
    const profiles = this.profiles();
    return profiles
      .filter(
        profile =>
          profile.profileName && profile.profileType === ProfileType.SUB_CA,
      )
      .map(profile => profile.profileName ?? '');
  });

  private signedBy = toSignal(
    this.form.controls[CaFormField.signedBy].valueChanges,
    { initialValue: this.signedByInitialValue },
  );

  profileNames = computed(() => {
    const signedBy = this.signedBy();
    const rootProfileNames = this.rootProfileNames();
    const subProfileNames = this.subProfileNames();
    // Profiles for current signedBy value https://bahar-limaye.atlassian.net/browse/XCA-260
    let profileNames = subProfileNames; // SUB_CA profiles by default;
    if (signedBy === this.selfSignedOption) profileNames = rootProfileNames; // self signed CA: root profiles
    return profileNames;
  });

  validCryptotokenKeys = computed(() => {
    const tokenName = this.tokenName();
    const cryptotokenKeys = this.cryptotokenKeys();
    if (cryptotokenKeys?.token === tokenName) {
      return cryptotokenKeys.keys;
    }
    return [];
  });

  private now = new Date();

  private maxExpirationDate(): Date {
    const date = new Date(this.now);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + ValidationConstants.maxCaValidityDays);
    return date;
  }

  private minExpirationDate(): Date {
    const date = new Date(this.now);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + ValidationConstants.minCaValidityDays);
    return date;
  }

  private createFormFieldOptions: Record<string, FormFieldOptions> = {
    [CaFormField.subjectDn]: FormFieldOptions.text({ required: true }),
    [CaFormField.signingAlgo]: FormFieldOptions.select({ required: true }),
    [CaFormField.validityUnits]: FormFieldOptions.select(),
    [CaFormField.validityDays]: FormFieldOptions.text({
      inputType: 'number',
      min: 1,
    }),
    [CaFormField.validityYears]: FormFieldOptions.text({
      inputType: 'number',
      min: 1,
    }),
    [CaFormField.expirationDate]: FormFieldOptions.datePicker({
      required: true,
      min: this.minExpirationDate(),
      max: this.maxExpirationDate(),
    }),
    [CaFormField.profileName]: this.profileNameOptions({ required: true }),
    [CaFormField.tokenName]: FormFieldOptions.autocomplete({
      required: true,
      strict: true,
    }),
    [CaFormField.signKey]: FormFieldOptions.autocomplete({
      required: true,
      strict: true,
    }),
    [CaFormField.signedBy]: FormFieldOptions.autocomplete({
      strict: true,
      required: true,
    }),
    [CaFormField.useLdapDnOrder]: FormFieldOptions.checkbox(),
  };

  private editFormFieldOptions: Record<string, FormFieldOptions> = {
    [CaFormField.expirationDate]: FormFieldOptions.datePicker({
      required: true,
      min: new Date(),
    }),
    [CaFormField.useLdapDnOrder]: FormFieldOptions.checkbox(),
  };

  constructor() {
    syncExpirationDateAndDuration({
      expirationDateControl: this.form.controls[CaFormField.expirationDate],
      validityUnitsControl: this.form.controls[CaFormField.validityUnits],
      validityDurationControls: {
        [ValidityUnit.day]: this.form.controls[CaFormField.validityDays],
        [ValidityUnit.year]: this.form.controls[CaFormField.validityYears],
      },
      syncIf: () => !this.edit(),
    })
      .pipe(takeUntilDestroyed())
      .subscribe();

    merge(this.disableProfileNameForExternal)
      .pipe(takeUntilDestroyed())
      .subscribe();

    effect(this.requestCryptotokenKeysEffect);
  }

  onCancel() {
    this.dialogService.withConfirmation({
      text: this.labels.confirmCancel,
      confirmIf: () => this.form.dirty,
    })(() => {
      this.cancelCreate.emit(undefined);
    });
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const dto = caFormValueToDto(this.form.value, this.selfSignedOption);
      this.create.emit(dto);
    }
  }

  onSubmitEdited() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      const dto = formValueToUpdateDto(this.form.value);
      const ca = this.editValue();
      if (ca) {
        this.update.emit({
          ca,
          dto,
        });
      }
    }
  }

  onCsrRequest() {
    const ca = this.editValue();
    const caName = ca?.caName ?? '';
    this.needCsr.emit({ caName });
  }

  onImportCertChain() {
    const ca = this.editValue();
    const caName = ca?.caName;
    const certChain = this.form.controls[CaFormField.certChain].value;
    if (caName && certChain) {
      this.importCertChain.emit({ caName, certChain });
    }
  }

  private requestCryptotokenKeysEffect = () => {
    const tokenName = this.tokenName();
    const controlValid = this.form.controls[CaFormField.tokenName].valid;
    if (tokenName && controlValid) {
      this.needCryptotokenKeys.emit({ tokenName });
    }
  };

  ngOnInit() {
    const edit = this.edit();
    const editValue = this.editValue();

    const formFieldOptions = edit
      ? this.editFormFieldOptions
      : this.createFormFieldOptions;

    const formFieldLabels = this.labels.inputs;

    this.formFieldService.configure({
      labels: formFieldLabels,
      options: formFieldOptions,
    });

    if (edit && editValue) {
      const formValue = caToFormValue(editValue, this.lang);
      this.form.patchValue(formValue, { emitEvent: false });
      this.disableUneditableFields();
    }
  }

  private disableUneditableFields() {
    Object.entries(this.form.controls).forEach(([name, control]) => {
      if (!this.editableFields.find(editableName => editableName === name)) {
        control.disable();
      }
    });
  }

  private profileNameOptions({
    required,
  }: {
    required: boolean;
  }): FormFieldOptions {
    return FormFieldOptions.autocomplete({ required, strict: true });
  }

  private disableProfileNameForExternal = this.form.controls[
    CaFormField.signedBy
  ].valueChanges.pipe(
    filter(() => !this.edit()),
    tap(signedBy => {
      const profileNameControl = this.form.controls[CaFormField.profileName];
      const profileNameEnabled = signedBy !== SIGNED_BY_EXTERNAL;
      profileNameControl.setValue('', {
        emitEvent: false,
      });
      if (profileNameEnabled) {
        profileNameControl.enable();
        this.formFieldService.updateControlOptions(
          CaFormField.profileName,
          this.profileNameOptions({ required: true }),
        );
      } else {
        profileNameControl.disable();
        this.formFieldService.updateControlOptions(
          CaFormField.profileName,
          this.profileNameOptions({ required: false }),
        );
      }
    }),
  );
}
